import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { sessionStore } from 'utils/storage'

interface UsersOnlineStore {
  usersOnline: number | undefined
  setUsersOnline: (count: number) => void
}

export const useUsersOnlineStore = create<UsersOnlineStore>()(
  persist(
    set => ({
      // Static placeholder so the indicator has something to render while the GraphQL
      // subscription connects. The real count overwrites this within a few hundred ms.
      // Using a constant (rather than a random) keeps screenshot tests deterministic;
      // tests can also seed `users_online_store` in `sessionStorage` to override it.
      usersOnline: 1750,
      setUsersOnline: count => set({ usersOnline: count }),
    }),
    {
      name: 'users_online_store',
      storage: {
        getItem: key => {
          const value = sessionStore.getString(key) ?? null
          return value ? JSON.parse(value) : null
        },
        setItem: sessionStore.setJSON,
        removeItem: sessionStore.remove,
      },
    },
  ),
)

/**
 * Read the online count from the Zustand store without setting up a subscription.
 * Safe to use in contexts where the subscription transport is not available (e.g. landing pages).
 * The value is kept up-to-date by any active `useUsersOnlineCount` subscriber on the page.
 */
export function useUsersOnlineValue() {
  return useUsersOnlineStore(s => s.usersOnline)
}
